import { FC, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import { FINANCEIRA_AUTH_CHANNEL_LABELS } from '../../../../../models/SimulationBank/SimulationBankModels';
import { expandSelectedCanonicalIds, FinanceiraTile, groupFinanceiraTiles, selectedTileIdsFromCanonicalIds } from './groupFinanceiraTiles';
import checkSelectIcon from '../../../../../assets/icons/checkselect.svg';
import './FinanceirasConsentModal.scss';

const TITLE_ID = 'financeiras-consent-modal-title';
const GRID_LABEL_ID = 'financeiras-consent-modal-grid-label';

interface HowStep {
    title: string;
    text: string;
}

// Textos fixos do card de autorização — não vêm da API, são o mesmo conteúdo pra todo
// parceiro.
const HOW_STEPS: HowStep[] = [
    { title: 'Selecione os bancos', text:'Escolha as instituições que deseja consultar.' },
    { title: 'Envie a autorização', text: 'Cada banco utiliza um canal para solicitar a autorização ao cliente, como WhatsApp, SMS, token ou link.' },
    { title: 'Oriente o cliente', text: 'Peça que ele conclua as autorizações recebidas. Após a autorização, a consulta da margem poderá ser realizada.' },
];

const authChannelLabel = (channel?: string | null): string | null => {
    if (!channel) return null;
    return FINANCEIRA_AUTH_CHANNEL_LABELS[channel as keyof typeof FINANCEIRA_AUTH_CHANNEL_LABELS] ?? channel;
};

interface FinanceiraTileOptionProps {
    tile: FinanceiraTile;
    selected: boolean;
    onToggle: (id: string) => void;
}

// Um tile do grid "Financeiras a Consultar" — extraído à parte (mesmo padrão do
// FinanceiraCardItem em Step3Cards.tsx) pra manter o componente principal legível.
const FinanceiraTileOption: FC<FinanceiraTileOptionProps> = ({ tile, selected, onToggle }) => {
    const channelLabel = authChannelLabel(tile.authChannel);

    const handleClick = () => onToggle(tile.id);
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onToggle(tile.id);
    };

    return (
        <div
            role="checkbox"
            aria-checked={selected}
            aria-label={tile.name}
            tabIndex={0}
            className={`sim-modal-financeira-tile${selected ? ' sim-modal-financeira-tile--selected' : ''}`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div className="sim-modal-financeira-tile__logo-wrap">
                {tile.logo ? (
                    <img src={tile.logo} alt="" className="sim-modal-financeira-tile__logo" />
                ) : (
                    <span className="sim-modal-financeira-tile__logo-fallback">{tile.name.slice(0, 2).toLowerCase()}</span>
                )}
            </div>
            <div className="sim-modal-financeira-tile__body">
                <span className="sim-modal-financeira-tile__name">{tile.name}</span>
                {channelLabel && <span className="sim-modal-financeira-tile__channel">{channelLabel}</span>}
            </div>
            {selected ? (
                <img src={checkSelectIcon} alt="" className="sim-modal-financeira-tile__radio-icon" />
            ) : (
                <span className="sim-modal-financeira-tile__radio" />
            )}
        </div>
    );
};

interface FinanceirasConsentModalProps {
    show: boolean;
    // Enquanto a triagem está em voo (handleAccept -> handleTriagem), desabilita as
    // ações pra evitar duplo submit — mesmo isLoadingTriagem que o modal antigo usava.
    isSubmitting: boolean;
    onBack: () => void;
    onAccept: (canonicalIds: string[]) => void;
}

const FinanceirasConsentModal: FC<FinanceirasConsentModalProps> = ({ show, isSubmitting, onBack, onAccept }) => {
    const { listFinanceirasParaConsulta, getLastSelectedFinancials } = useSimulationBank();
    const [tiles, setTiles] = useState<FinanceiraTile[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Busca a lista a cada abertura do modal — nunca fixa no código (CA da tarefa): uma
    // financeira nova cadastrada no admin aparece aqui sem deploy do front.
    useEffect(() => {
        if (!show) return;
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            const [options, lastSelectedCanonicalIds] = await Promise.all([
                listFinanceirasParaConsulta(),
                getLastSelectedFinancials(),
            ]);
            if (cancelled) return;

            // O endpoint devolve financeiras inativas de propósito (usado também em outro
            // lugar do fluxo) — o filtro por is_active é só local, aqui no modal.
            const activeOptions = options.filter((f) => f.is_active !== false);
            const nextTiles = groupFinanceiraTiles(activeOptions);
            setTiles(nextTiles);

            // Usuário sem simulação anterior (ou sem `selected_financials` salvo): mantém o
            // comportamento de sempre, com todos os tiles marcados. Havendo histórico, marca
            // só o que foi usado da última vez.
            setSelected(
                lastSelectedCanonicalIds.length > 0
                    ? selectedTileIdsFromCanonicalIds(nextTiles, lastSelectedCanonicalIds)
                    : new Set(nextTiles.map((t) => t.id))
            );
            setIsLoading(false);
        };

        load();
        return () => { cancelled = true; };
    }, [show, listFinanceirasParaConsulta, getLastSelectedFinancials]);

    // Foco inicial no diálogo ao abrir — acessibilidade básica de modal.
    useEffect(() => {
        if (show) dialogRef.current?.focus();
    }, [show]);

    // Quando o modal não cabe no card (zoom alto do navegador), abre já rolado pro fim —
    // a parte que o usuário precisa (seleção das financeiras e botões de ação) fica à
    // vista, e o texto explicativo do topo continua acessível rolando pra cima. Roda depois
    // do carregamento porque é só aí que o grid entra e a altura final do conteúdo existe.
    useEffect(() => {
        if (!show || isLoading) return;
        const dialog = dialogRef.current;
        if (dialog) dialog.scrollTop = dialog.scrollHeight;
    }, [show, isLoading]);

    // Esc fecha o modal (mesmo caminho de "Voltar": preserva os dados do formulário).
    useEffect(() => {
        if (!show) return;
        const handleKeyDown = (e: globalThis.KeyboardEvent) => {
            if (e.key === 'Escape') onBack();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [show, onBack]);

    if (!show) return null;

    const toggleTile = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Alterna entre marcar e desmarcar tudo: com alguma financeira selecionada, o controle
    // some com todas; já vazio, ele volta a marcar todas — o rótulo e o ícone de check
    // acompanham qual das duas ações o clique vai disparar.
    const hasSelection = selected.size > 0;
    const handleToggleSelectAll = () => {
        setSelected(hasSelection ? new Set() : new Set(tiles.map((t) => t.id)));
    };

    const handleAccept = () => {
        if (selected.size === 0) return;
        onAccept(expandSelectedCanonicalIds(tiles, selected));
    };

    // Clique no overlay (fora do card) fecha o modal, igual ao "Voltar" — mas só quando o
    // clique começou no próprio overlay, não num elemento do conteúdo que borbulhou até ele.
    const handleOverlayMouseDown = (e: MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onBack();
    };

    const busy = isSubmitting;
    const allSelected = tiles.length > 0 && selected.size === tiles.length;

    return createPortal(
        <div className="sim-modal-overlay" onMouseDown={handleOverlayMouseDown}>
            <div
                className="sim-modal sim-modal--financeiras"
                role="dialog"
                aria-modal="true"
                aria-labelledby={TITLE_ID}
                tabIndex={-1}
                ref={dialogRef}
            >
                {/* Só o corpo rola — o rodapé com as ações fica fora dele pra continuar visível
                    mesmo quando o modal não cabe no card (ex.: zoom alto do navegador). */}
                <div className="sim-modal-financeiras-body">
                    <h4 className="sim-modal-title" id={TITLE_ID}>Autorizar consulta de margem</h4>
                    <p className="sim-modal-text">
                        Para consultar a margem na Dataprev e gerar ofertas, o cliente precisa autorizar as instituições selecionadas.
                    </p>

                    <div className="sim-modal-intro-grid">
                        <div className="sim-modal-how">
                            <h5 className="sim-modal-how__title">Como funciona</h5>
                            <ol className="sim-modal-how__list">
                                {HOW_STEPS.map((step, index) => (
                                    <li key={step.title} className="sim-modal-how__item">
                                        <span className="sim-modal-how__badge">{index + 1}</span>
                                        <div className="sim-modal-how__body">
                                            <strong>{step.title}</strong>
                                            <p>{step.text}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <div className="sim-modal-privacy">
                            <div className="sim-modal-privacy__header">
                                <img
                                    src={`${process.env.PUBLIC_URL}/assets/img/securityIcon.svg`}
                                    alt=""
                                    className="sim-modal-privacy__icon"
                                />
                                <strong>Privacidade e controle</strong>
                            </div>
                            <p>A autorização serve apenas para consultar a margem e pode ser revogada pelo cliente a qualquer momento.</p>
                        </div>
                    </div>

                    <p className="sim-modal-important-note">
                        <strong>Importante:</strong> WhatsApp e SMS são enviados diretamente ao celular do cliente. Quando houver link, ele será disponibilizado na próxima tela.
                    </p>

                    <hr className="sim-modal-divider" />

                    <div className="sim-modal-financeiras-header">
                        <div className="sim-modal-financeiras-header__top">
                            <span className="sim-modal-financeiras-title" id={GRID_LABEL_ID}>Financeiras a Consultar</span>

                            {tiles.length > 0 && (
                                <div className="sim-modal-financeiras-summary">
                                    <span className="sim-modal-financeiras-count">{selected.size} de {tiles.length} selecionados</span>
                                    <button
                                        type="button"
                                        className="sim-modal-financeiras-clear"
                                        onClick={handleToggleSelectAll}
                                        disabled={busy}
                                    >
                                        {hasSelection ? (
                                            <img src={checkSelectIcon} alt="" className="sim-modal-financeiras-clear__icon" />
                                        ) : (
                                            <span className="sim-modal-financeiras-clear__icon sim-modal-financeiras-clear__icon--empty" />
                                        )}
                                        {hasSelection ? 'Desmarcar todos' : 'Marcar todos'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="sim-modal-financeiras-loading">
                            <Spinner animation="border" size="sm" /> Carregando financeiras...
                        </div>
                    ) : tiles.length === 0 ? (
                        <p className="sim-modal-financeiras-empty">Nenhuma financeira disponível no momento.</p>
                    ) : (
                        <div className="sim-modal-financeiras-grid" role="group" aria-labelledby={GRID_LABEL_ID}>
                            {tiles.map((tile) => (
                                <FinanceiraTileOption
                                    key={tile.id}
                                    tile={tile}
                                    selected={selected.has(tile.id)}
                                    onToggle={toggleTile}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="sim-modal-actions sim-modal-actions--financeiras">
                    <button type="button" className="btn btn-ghost-sim" onClick={onBack} disabled={busy}>
                        Voltar
                    </button>
                    <button
                        type="button"
                        className="btn btn-simulation-primary"
                        onClick={handleAccept}
                        disabled={busy || selected.size === 0}
                    >
                        {busy ? <Spinner size="sm" /> : 'Enviar/Gerar Autorização'}
                    </button>
                </div>
            </div>
        </div>,
        document.getElementById('chats-card') ?? document.body
    );
};

export default FinanceirasConsentModal;
